const SAFE_ERROR_MESSAGES: Record<string, string> = {
  'duplicate key value violates unique constraint': 'This item already exists.',
  'violates foreign key constraint': 'Unable to complete operation due to related data.',
  'violates check constraint': 'The provided data does not meet requirements.',
  'permission denied': 'You do not have permission to perform this action.',
  'jwt expired': 'Your session has expired. Please sign in again.',
  'invalid jwt': 'Authentication failed. Please sign in again.',
  'network error': 'Network error. Please check your connection and try again.',
  'fetch failed': 'Network error. Please check your connection and try again.',
};

const VALIDATION_ERROR_PATTERNS = [
  /invalid.*email/i,
  /email.*invalid/i,
  /password.*weak/i,
  /password.*short/i,
  /required field/i,
  /must be at least/i,
  /must be at most/i,
  /exceeds maximum/i,
];

const USER_FRIENDLY_MESSAGES: Record<string, string> = {
  duplicate_email: 'This email is already registered.',
  duplicate_slug: 'This URL slug is already in use.',
  invalid_credentials: 'Invalid email or password.',
  rate_limit: 'Too many attempts. Please try again later.',
  validation_error: 'Please check your input and try again.',
  database_error: 'An error occurred. Please try again.',
  network_error: 'Network error. Please check your connection and try again.',
  unknown_error: 'An error occurred. Please try again.',
};

function isValidationError(errorMessage: string): boolean {
  return VALIDATION_ERROR_PATTERNS.some(pattern => pattern.test(errorMessage));
}

function findSafeMessage(errorMessage: string): string | null {
  const lowerMessage = errorMessage.toLowerCase();

  for (const [pattern, safeMessage] of Object.entries(SAFE_ERROR_MESSAGES)) {
    if (lowerMessage.includes(pattern)) {
      return safeMessage;
    }
  }

  return null;
}

export function sanitizeError(error: unknown, context?: string): string {
  let errorMessage = 'An error occurred. Please try again.';

  if (!error) {
    return errorMessage;
  }

  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'object' && 'message' in error) {
    errorMessage = String((error as any).message);
  }

  if (isValidationError(errorMessage)) {
    return errorMessage;
  }

  const safeMessage = findSafeMessage(errorMessage);
  if (safeMessage) {
    return safeMessage;
  }

  if (errorMessage.includes('unique constraint') && errorMessage.includes('email')) {
    return USER_FRIENDLY_MESSAGES.duplicate_email;
  }

  if (errorMessage.includes('unique constraint') && errorMessage.includes('slug')) {
    return USER_FRIENDLY_MESSAGES.duplicate_slug;
  }

  if (errorMessage.includes('23505')) {
    return 'This item already exists.';
  }

  if (errorMessage.includes('23503')) {
    return 'Unable to complete operation due to related data.';
  }

  if (errorMessage.includes('23514')) {
    return 'The provided data does not meet requirements.';
  }

  if (errorMessage.includes('JWT') || errorMessage.includes('jwt') || errorMessage.includes('token')) {
    return 'Your session has expired. Please sign in again.';
  }

  if (errorMessage.includes('permission') || errorMessage.includes('authorized')) {
    return 'You do not have permission to perform this action.';
  }

  console.error(`[${context || 'Error'}]:`, errorMessage);

  return USER_FRIENDLY_MESSAGES.unknown_error;
}

export function logError(context: string, error: unknown, additionalInfo?: Record<string, any>): void {
  if (error instanceof Error) {
    console.error(`[${context}]:`, error.message, error);
  } else {
    console.error(`[${context}]:`, error);
  }

  if (additionalInfo) {
    console.error(`[${context}] Additional info:`, additionalInfo);
  }
}

export { USER_FRIENDLY_MESSAGES };
